// SideDrawer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useSwipeGesture, {
  DEFAULT_DRAWER_WIDTH,
  SWIPE_SIDE,
} from '../hook/useSwipeGesture.js';
import ModalTracking from '../component/ModalTracking.jsx';
import { NULL_FUNCTION } from '../resource/defaults.js';

// Minimal component that absorbs the setModalTracking prop injected by
// ModalTracking's cloneElement, preventing unknown-prop DOM warnings.
const ModalTrackingAnchor = () => null;

const PORTAL_Z_INDEX = 9999; // align with #modal-root z-index
const CLOSE_ANIMATION_MS = 300; // align with modal animation duration
const TRANSITION_CSS = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'; // align with modal animation duration
const BACKDROP_TRANSITION_CSS = 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)'; // align with modal animation duration
const HORIZONTAL_GESTURE_THRESHOLD = 10; // align with modal animation duration

const getModalRoot = () =>
  document.querySelector('#modal-root') || document.body;

// ---------------------------------------------------------------------------
// SideDrawerPanel — pure visual layer (portal, CSS transitions, delayed unmount)
// ---------------------------------------------------------------------------

const SideDrawerPanel = ({
  isOpen = false,
  isVisible = false,
  dragOffset = null,
  dragProgress = null,
  isDragging = false,
  isOpening = false,
  closeBind,
  handleClose,
  setDrawerEl,
  width = DEFAULT_DRAWER_WIDTH,
  side = SWIPE_SIDE.RIGHT,
  isTransparentMask = false,
  children = null,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const contentRef = useRef(null);
  const touchStateRef = useRef({
    startX: 0,
    startY: 0,
    locked: false,
    horizontal: false,
  });

  // Detect horizontal gesture independently (no React state dependency).
  // Prevents vertical content scroll (e.g. FeedWrapper) during a close-drag.
  function handleContentTouchStart(e) {
    const t = e.touches[0];
    touchStateRef.current = {
      startX: t.clientX,
      startY: t.clientY,
      locked: false,
      horizontal: false,
    };
  }

  function handleContentTouchMove(e) {
    const s = touchStateRef.current;
    if (!s.locked) {
      const dx = Math.abs(e.touches[0].clientX - s.startX);
      const dy = Math.abs(e.touches[0].clientY - s.startY);
      if (
        dx > HORIZONTAL_GESTURE_THRESHOLD ||
        dy > HORIZONTAL_GESTURE_THRESHOLD
      ) {
        s.locked = true;
        s.horizontal = dx > dy;
      }
    }
    if (s.horizontal) {
      e.preventDefault();
    }
  }

  // Callback ref — React calls this with the DOM node on mount, null on
  // unmount, or a new node if the element is replaced.
  const setContentRef = useCallback(node => {
    // Unmount or replace: clean up listeners on the previous DOM element.
    if (contentRef.current) {
      contentRef.current.removeEventListener(
        'touchstart',
        handleContentTouchStart
      );
      contentRef.current.removeEventListener(
        'touchmove',
        handleContentTouchMove
      );
    }
    contentRef.current = node;
    // Mount or replace: register listeners on the new DOM element.
    if (node) {
      node.addEventListener('touchstart', handleContentTouchStart, {
        passive: true,
      });
      node.addEventListener('touchmove', handleContentTouchMove, {
        passive: false,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen || isDragging) {
      setIsMounted(true);
    } else if (isMounted) {
      const timer = setTimeout(() => setIsMounted(false), CLOSE_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isDragging, isMounted]);

  if (!isMounted) return null;

  let drawerTransform;
  let drawerTransition;
  let backdropOpacity;
  let backdropTransition;

  if (isDragging) {
    const sign = side === SWIPE_SIDE.RIGHT ? 1 : -1;
    drawerTransform = `translateX(${sign * dragOffset}px)`;
    drawerTransition = 'none';
    backdropOpacity = isTransparentMask
      ? 0
      : dragProgress != null
        ? dragProgress
        : 0;
    backdropTransition = 'none';
  } else {
    const closedOffset = side === SWIPE_SIDE.RIGHT ? '100%' : '-100%';
    drawerTransform = `translateX(${isVisible ? '0' : closedOffset})`;
    drawerTransition = TRANSITION_CSS;
    backdropOpacity = isTransparentMask ? 0 : isVisible ? 1 : 0;
    backdropTransition = BACKDROP_TRANSITION_CSS;
  }

  const closeBindProps = typeof closeBind === 'function' ? closeBind() : {};

  return createPortal(
    <PortalWrapper $isOpening={isOpening}>
      <Overlay>
        <Backdrop
          $opacity={backdropOpacity}
          $transition={backdropTransition}
          $isTransparentMask={isTransparentMask}
          onClick={handleClose}
        />
        <DrawerWrapper
          ref={setDrawerEl}
          $width={width}
          $side={side}
          $transform={drawerTransform}
          $transition={drawerTransition}
        >
          <DrawerPanel ref={setContentRef} {...closeBindProps}>
            {children}
          </DrawerPanel>
        </DrawerWrapper>
      </Overlay>
    </PortalWrapper>,
    getModalRoot()
  );
};

SideDrawerPanel.displayName = 'SideDrawerPanel';

SideDrawerPanel.propTypes = {
  isOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  dragOffset: PropTypes.number,
  dragProgress: PropTypes.number,
  isDragging: PropTypes.bool,
  isOpening: PropTypes.bool,
  closeBind: PropTypes.func,
  handleClose: PropTypes.func,
  setDrawerEl: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  side: PropTypes.oneOf([SWIPE_SIDE.LEFT, SWIPE_SIDE.RIGHT]),
  isTransparentMask: PropTypes.bool,
  children: PropTypes.node,
};

// ---------------------------------------------------------------------------
// SideDrawer — swipeable side drawer with Mixpanel tracking.
//
// Controlled component: parent owns `isOpen` state, SideDrawer notifies
// via `onOpen` / `onClose` callbacks.
//
// @example
// ```
// const [isOpen, setIsOpen] = useState(false);
// <SideDrawer
//   isOpen={isOpen}
//   containerRef={parentRef}
//   isEnabled
//   trackingModalId="modal-explore-more-livestream"
//   width="90vw"
//   isTransparentMask
//   onOpen={() => setIsOpen(true)}
//   onClose={() => setIsOpen(false)}
// >
//   <PanelContent />
// </SideDrawer>
// ```
// ---------------------------------------------------------------------------

const SideDrawer = ({
  isOpen: isOpenProp = false,
  containerRef,
  isEnabled = true,
  side = SWIPE_SIDE.RIGHT,
  width = 333,
  trackingModalId = '',
  isTransparentMask = false,
  onOpen = NULL_FUNCTION,
  onClose = NULL_FUNCTION,
  onDragStateChange = NULL_FUNCTION,
  children = null,
}) => {
  const { drawerProps } = useSwipeGesture({
    containerRef,
    isEnabled,
    side,
    width,
    isOpen: isOpenProp,
    onOpen,
    onClose,
    onDragStateChange,
  });

  return (
    <>
      <SideDrawerPanel {...drawerProps} isTransparentMask={isTransparentMask}>
        {children}
      </SideDrawerPanel>
      {isOpenProp && trackingModalId && (
        <ModalTracking modalId={trackingModalId}>
          <ModalTrackingAnchor />
        </ModalTracking>
      )}
    </>
  );
};

SideDrawer.displayName = 'SideDrawer';

SideDrawer.propTypes = {
  isOpen: PropTypes.bool,
  containerRef: PropTypes.object,
  isEnabled: PropTypes.bool,
  side: PropTypes.oneOf([SWIPE_SIDE.LEFT, SWIPE_SIDE.RIGHT]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  trackingModalId: PropTypes.string,
  isTransparentMask: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onDragStateChange: PropTypes.func,
  children: PropTypes.node,
};

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PortalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${PORTAL_Z_INDEX};
  pointer-events: ${({ $isOpening }) => ($isOpening ? 'none' : 'auto')};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${({ $isTransparentMask }) =>
    $isTransparentMask ? 'transparent' : 'rgba(0, 0, 0, 0.7)'};
  opacity: ${({ $opacity }) => $opacity};
  transition: ${({ $transition }) => $transition};
`;

const DrawerWrapper = styled.div`
  position: absolute;
  top: 0;
  ${({ $side }) => ($side === SWIPE_SIDE.LEFT ? 'left: 0;' : 'right: 0;')}
  height: 100%;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width};
  transform: ${({ $transform }) => $transform};
  transition: ${({ $transition }) => $transition};
  overflow: clip;
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const DrawerPanel = styled.div`
  height: 100%;
  width: 100%;
  touch-action: pan-y;
`;

export { SideDrawerPanel, SWIPE_SIDE, PORTAL_Z_INDEX };
export default SideDrawer;
